import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Clock,
  MessageCircle,
  Files,
  Phone,
  CalendarCheck,
  Sparkles,
  X,
  RefreshCw,
  Loader2,
  Moon,
  Sun,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { scheduledTasksApi } from "@/lib/crmApi";
import { useSettings } from "@/context/SettingsContext";
import { cn } from "@/lib/utils";

const KIND_META = {
  send_welcome: {
    icon: Sparkles,
    label: "Bienvenue",
    cls: "bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300",
  },
  send_text: {
    icon: MessageCircle,
    label: "Message texte",
    cls: "bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300",
  },
  send_document: {
    icon: Files,
    label: "Document",
    cls: "bg-violet-50 text-violet-700 dark:bg-violet-950/40 dark:text-violet-300",
  },
  follow_up: {
    icon: Phone,
    label: "Relance 4h",
    cls: "bg-orange-50 text-orange-700 dark:bg-orange-950/40 dark:text-orange-300",
  },
  appointment_reminder: {
    icon: CalendarCheck,
    label: "Rappel veille RDV",
    cls: "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300",
  },
};

function fmtRunAt(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("fr-FR", {
      weekday: "short",
      day: "2-digit",
      month: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

// Returns "aujourd'hui", "demain" or a weekday so the quiet-hours indicator
// makes the date readable at a glance — "sam. 09/05" alone confused users
// who didn't realise the time meant the NEXT day (BUG-14).
function relativeDay(iso) {
  if (!iso) return "";
  try {
    const target = new Date(iso);
    const today = new Date();
    const startOfDay = (d) =>
      new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
    const diff = Math.round(
      (startOfDay(target) - startOfDay(today)) / (24 * 3600 * 1000)
    );
    if (diff === 0) return "aujourd'hui";
    if (diff === 1) return "demain";
    if (diff === -1) return "hier";
    return target.toLocaleDateString("fr-FR", { weekday: "long" });
  } catch {
    return "";
  }
}

function relativeTo(iso) {
  if (!iso) return "";
  try {
    const diff = new Date(iso).getTime() - Date.now();
    if (diff <= 0) return "imminent";
    const mins = Math.round(diff / 60000);
    if (mins < 60) return `dans ${mins} min`;
    const h = Math.round(mins / 60);
    if (h < 24) return `dans ${h}h`;
    const days = Math.round(h / 24);
    return `dans ${days}j`;
  } catch {
    return "";
  }
}

export default function ScheduledTasksPanel() {
  const settings = useSettings() || {};
  // Scope the queue to the active project so each project shows only its own
  // pending sends. null ("Tous les projets") = global view, unchanged.
  const activeProjectId = settings.activeProjectId || null;
  const activeProjectNom = settings.activeProject?.nom || "";
  const [tasks, setTasks] = useState([]);
  const [windowState, setWindowState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [t, w] = await Promise.all([
        scheduledTasksApi.list({ status: "pending", projetId: activeProjectId }),
        scheduledTasksApi.windowStatus(),
      ]);
      setTasks(Array.isArray(t) ? t : []);
      setWindowState(w);
    } catch (e) {
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  }, [activeProjectId]);

  useEffect(() => {
    load();
    const id = setInterval(load, 60_000); // refresh every minute
    return () => clearInterval(id);
  }, [load]);

  const cancel = async (task) => {
    if (
      !window.confirm(
        `Annuler la tâche « ${task.kind_label} » prévue ${fmtRunAt(task.run_at)} ?`
      )
    )
      return;
    setCancelling(task.id);
    try {
      await scheduledTasksApi.cancel(task.id);
      toast.success("Tâche annulée");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setCancelling(null);
    }
  };

  // Group tasks by lead so we don't show 4 lines in a row for the same lead
  const grouped = useMemo(() => {
    const map = new Map();
    for (const t of tasks) {
      const key = t.lead_id || `_no_lead_${t.id}`;
      if (!map.has(key)) {
        map.set(key, { lead_id: t.lead_id, lead_nom: t.lead_nom, lead_telephone: t.lead_telephone, items: [] });
      }
      map.get(key).items.push(t);
    }
    return Array.from(map.values());
  }, [tasks]);

  const inWindow = windowState?.can_send_now ?? true;

  return (
    <section
      className="rounded-2xl border border-border/60 bg-card p-5 shadow-soft sm:p-6"
      data-testid="scheduled-tasks-panel"
    >
      <header className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-indigo-50 text-indigo-700 dark:bg-indigo-950/40 dark:text-indigo-300">
            <Clock className="h-5 w-5" />
          </div>
          <div>
            <h2 className="font-heading text-lg font-bold">
              Envois programmés
              {activeProjectNom && (
                <span className="ml-2 align-middle text-xs font-semibold text-[hsl(var(--accent-gold))]">
                  · {activeProjectNom}
                </span>
              )}
            </h2>
            <p className="text-xs text-muted-foreground">
              {tasks.length} tâche{tasks.length > 1 ? "s" : ""} en attente
              {activeProjectNom ? " sur ce projet" : " (tous projets)"}
              {" · refresh auto toutes les minutes"}
            </p>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={load}
          disabled={loading}
          className="gap-1.5"
          data-testid="scheduled-refresh"
        >
          <RefreshCw className={cn("h-3.5 w-3.5", loading && "animate-spin")} />
          Rafraîchir
        </Button>
      </header>

      {/* Quiet-hours indicator */}
      <div
        className={cn(
          "mb-4 flex items-start gap-2 rounded-lg border px-3 py-2 text-xs",
          inWindow
            ? "border-emerald-300 bg-emerald-50/50 text-emerald-900 dark:border-emerald-800 dark:bg-emerald-950/20 dark:text-emerald-200"
            : "border-zinc-300 bg-zinc-50 text-zinc-800 dark:border-zinc-700 dark:bg-zinc-900/40 dark:text-zinc-200"
        )}
      >
        {inWindow ? (
          <Sun className="mt-0.5 h-3.5 w-3.5 flex-none" />
        ) : (
          <Moon className="mt-0.5 h-3.5 w-3.5 flex-none" />
        )}
        <div className="flex-1">
          {inWindow ? (
            <>
              <b>Heures d'envoi actives.</b>{" "}
              Les nouvelles tâches partent immédiatement (fenêtre {windowState?.window_label}).
            </>
          ) : (
            <>
              <b>Heures de silence en cours.</b>{" "}
              Les nouvelles automatisations sont reportées au prochain créneau autorisé :{" "}
              <b>{fmtRunAt(windowState?.next_allowed_at)}</b>
              {windowState?.next_allowed_at && (
                <span className="text-muted-foreground">
                  {" "}({relativeDay(windowState.next_allowed_at)})
                </span>
              )}{" "}
              ({windowState?.window_label}).
            </>
          )}
        </div>
      </div>

      {loading && tasks.length === 0 ? (
        <div className="flex items-center justify-center py-12 text-sm text-muted-foreground">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          Chargement…
        </div>
      ) : tasks.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border/60 p-8 text-center">
          <Clock className="mx-auto h-8 w-8 text-muted-foreground/40" />
          <p className="mt-2 font-heading text-sm font-bold">Aucun envoi programmé</p>
          <p className="mt-1 text-xs text-muted-foreground">
            Les automatisations (welcome, relance 4h, rappel RDV…) apparaîtront ici
            avant leur exécution.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {grouped.map((g) => (
            <div
              key={g.lead_id || g.items[0]?.id}
              className="rounded-xl border border-border/60 bg-card p-3"
              data-testid={`scheduled-group-${g.lead_id || "noLead"}`}
            >
              <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                {g.lead_id ? (
                  <Link
                    to={`/leads/${g.lead_id}`}
                    className="font-heading text-sm font-bold hover:underline"
                  >
                    {g.lead_nom || "Lead sans nom"}
                  </Link>
                ) : (
                  <span className="font-heading text-sm font-bold text-muted-foreground italic">
                    (sans lead associé)
                  </span>
                )}
                {g.lead_telephone && (
                  <span className="text-[11px] text-muted-foreground">{g.lead_telephone}</span>
                )}
              </div>
              <div className="space-y-1.5">
                {g.items.map((t) => {
                  const meta = KIND_META[t.kind] || {
                    icon: Clock,
                    label: t.kind_label || t.kind,
                    cls: "bg-muted text-foreground",
                  };
                  const Icon = meta.icon;
                  return (
                    <div
                      key={t.id}
                      className="flex flex-wrap items-center gap-2 rounded-lg border border-border/40 bg-background px-2.5 py-1.5 text-xs"
                      data-testid={`scheduled-task-${t.id}`}
                    >
                      <span
                        className={cn(
                          "inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[11px] font-semibold",
                          meta.cls
                        )}
                      >
                        <Icon className="h-3 w-3" />
                        {meta.label}
                      </span>
                      <span className="text-muted-foreground">
                        Prévu <b className="text-foreground">{fmtRunAt(t.run_at)}</b>
                        {" "}
                        <span className="text-[10px]">({relativeTo(t.run_at)})</span>
                      </span>
                      {t.attempts > 1 && (
                        <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] text-amber-800 dark:bg-amber-950/40 dark:text-amber-300">
                          {t.attempts} tentatives
                        </span>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        className="ml-auto h-6 w-6 p-0 text-muted-foreground hover:text-red-500"
                        onClick={() => cancel(t)}
                        disabled={cancelling === t.id}
                        title="Annuler"
                        data-testid={`scheduled-cancel-${t.id}`}
                      >
                        {cancelling === t.id ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <X className="h-3 w-3" />
                        )}
                      </Button>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
